<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { Project } from "@/types";
import {
  useProjectDatastores,
  useSetProjectDatastoreValue,
  type ProjectDatastoreEntryView,
} from "@/queries/projects";

interface Props {
  project: Project | null;
}

const props = defineProps<Props>();
const projectIdRef = computed(() => props.project?.id ?? "");
const enabledRef = computed(() => !!projectIdRef.value);

const { datastores, datastoresLoading, datastoresError, refetchDatastores } =
  useProjectDatastores(projectIdRef, enabledRef);
const { setDatastoreValueAsync, setDatastoreValuePending, setDatastoreValueError } =
  useSetProjectDatastoreValue(projectIdRef);

// Per-row draft state — keyed by datastore name. The value editor is a textarea over the JSON
// (pretty-printed). On save we parse the JSON; parse errors are surfaced inline.
const drafts = ref<Record<string, string>>({});
const parseErrors = ref<Record<string, string | null>>({});
const pendingName = ref<string | null>(null);

// Hydrate drafts whenever the server-side data lands.
watch(
  () => datastores.value?.datastores,
  (list) => {
    if (!list) return;
    const fresh: Record<string, string> = {};
    for (const entry of list) {
      fresh[entry.name] = serialize(entry.value);
    }
    drafts.value = fresh;
    parseErrors.value = {};
  },
  { immediate: true }
);

function serialize(value: unknown): string {
  if (value === null || value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function hasChange(entry: ProjectDatastoreEntryView): boolean {
  return drafts.value[entry.name] !== serialize(entry.value);
}

async function save(entry: ProjectDatastoreEntryView) {
  const raw = drafts.value[entry.name] ?? "";
  let parsed: unknown;
  try {
    parsed = raw.trim() === "" ? null : JSON.parse(raw);
    parseErrors.value[entry.name] = null;
  } catch (e) {
    parseErrors.value[entry.name] = (e as Error).message || "Invalid JSON";
    return;
  }
  pendingName.value = entry.name;
  try {
    await setDatastoreValueAsync({ name: entry.name, value: parsed });
    await refetchDatastores();
  } catch {
    // Surfaced via setDatastoreValueError below.
  } finally {
    pendingName.value = null;
  }
}

function revert(entry: ProjectDatastoreEntryView) {
  drafts.value[entry.name] = serialize(entry.value);
  parseErrors.value[entry.name] = null;
}
</script>

<template>
  <div class="tab-pane fade show active">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Data Stores</h2>
        <p class="text-muted mb-0">
          Each datastore is a free-form JSON value the workflow author can navigate at runtime
          (e.g. <code>{{ '{' }}{{ '{' }} datastore.customers[0].name {{ '}' }}{{ '}' }}</code>).
        </p>
      </div>
    </div>

    <div v-if="datastoresLoading" class="text-center text-muted py-4">
      <div class="spinner-border" role="status"></div>
    </div>

    <div v-else-if="datastoresError" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Failed to load datastores. {{ datastoresError.userMessage }}
    </div>

    <div v-else-if="(datastores?.datastores ?? []).length === 0" class="alert alert-info">
      <i class="bi bi-info-circle me-2"></i>
      No datastores are declared by this project's workflows yet. Declarations appear automatically
      when a service references <code>datastore.&lt;name&gt;</code>.
    </div>

    <div v-else>
      <div
        v-for="entry in datastores!.datastores"
        :key="entry.name"
        class="card mb-3"
      >
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span class="fw-semibold">{{ entry.name }}</span>
              <small v-if="entry.description" class="text-muted ms-2">— {{ entry.description }}</small>
            </div>
            <div class="d-flex gap-2">
              <button
                class="bb-btn-secondary-cta btn-sm"
                :disabled="!hasChange(entry) || pendingName === entry.name"
                @click="revert(entry)"
              >
                Revert
              </button>
              <button
                class="bb-btn-primary-cta btn-sm"
                :disabled="!hasChange(entry) || pendingName === entry.name"
                @click="save(entry)"
              >
                <span
                  v-if="pendingName === entry.name && setDatastoreValuePending"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                <i v-else class="bi bi-check-circle me-1"></i>
                Save
              </button>
            </div>
          </div>

          <textarea
            v-model="drafts[entry.name]"
            class="form-control font-monospace small"
            rows="6"
            spellcheck="false"
            :placeholder="'{\n  \&quot;example\&quot;: \&quot;value\&quot;\n}'"
          ></textarea>

          <div v-if="parseErrors[entry.name]" class="text-danger small mt-1">
            <i class="bi bi-exclamation-triangle me-1"></i>
            {{ parseErrors[entry.name] }}
          </div>
        </div>
      </div>

      <div v-if="setDatastoreValueError" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ setDatastoreValueError.userMessage }}
      </div>
    </div>
  </div>
</template>
